import { Model } from '@nozbe/watermelondb'
import { field, date, readonly, relation, children } from '@nozbe/watermelondb/decorators'

export class Product extends Model {
    static table = 'products'
    static associations = {
        stores: { type: 'belongs_to', key: 'store_id' },
        categories: { type: 'belongs_to', key: 'category_id' },
        line_items: { type: 'has_many', foreignKey: 'product_id' },
        product_variants: { type: 'has_many', foreignKey: 'product_id' },
        batches: { type: 'has_many', foreignKey: 'product_id' },
        stock_movements: { type: 'has_many', foreignKey: 'product_id' },
    } as const

    @field('store_id') storeId!: string
    @field('category_id') categoryId!: string | null
    @field('supplier_id') supplierId!: string | null
    @field('name') name!: string
    @field('name_hindi') nameHindi!: string | null
    @field('description') description!: string | null
    @field('sku') sku!: string | null
    @field('barcode') barcode!: string | null
    @field('hsn_code') hsnCode!: string | null
    @field('mrp') mrp!: number
    @field('selling_price') sellingPrice!: number
    @field('purchase_price') purchasePrice!: number
    @field('wholesale_price') wholesalePrice!: number | null
    @field('tax_profile_id') taxProfileId!: string | null
    @field('gst_rate') gstRate!: number
    @field('cess_rate') cessRate!: number
    @field('unit') unit!: string
    @field('secondary_unit') secondaryUnit!: string | null
    @field('conversion_rate') conversionRate!: number | null
    @field('current_stock') currentStock!: number
    @field('low_stock_alert') lowStockAlert!: number
    @field('track_inventory') trackInventory!: boolean
    @field('is_service') isService!: boolean
    @field('image_url') imageUrl!: string | null
    @field('is_active') isActive!: boolean
    @field('version') version!: number
    @field('is_synced') isSynced!: boolean
    @field('is_deleted') isDeleted!: boolean
    @field('last_sync_current_stock') lastSyncCurrentStock!: number | null
    @readonly @date('created_at') createdAt!: Date
    @readonly @date('updated_at') updatedAt!: Date

    @relation('stores', 'store_id') store: any
    @relation('categories', 'category_id') category: any
    @children('product_variants') variants: any
    @children('batches') batches: any
    @children('stock_movements') stockMovements: any
    @children('line_items') lineItems: any
}

export class Category extends Model {
    static table = 'categories'
    @field('merchant_id') merchantId!: string
    @field('name') name!: string
    @field('parent_id') parentId!: string | null
    @field('sort_order') sortOrder!: number
    @field('version') version!: number
    @field('is_synced') isSynced!: boolean
    @field('is_deleted') isDeleted!: boolean
    @readonly @date('created_at') createdAt!: Date
    @readonly @date('updated_at') updatedAt!: Date
}

export class ProductVariant extends Model {
    static table = 'product_variants'
    @field('product_id') productId!: string
    @field('name') name!: string
    @field('sku') sku!: string | null
    @field('barcode') barcode!: string | null
    @field('price_adj') priceAdj!: number
    @field('stock') stock!: number
    @field('version') version!: number
    @field('is_synced') isSynced!: boolean
    @field('is_deleted') isDeleted!: boolean
    @readonly @date('created_at') createdAt!: Date
    @readonly @date('updated_at') updatedAt!: Date
}

export class Batch extends Model {
    static table = 'batches'
    @field('product_id') productId!: string
    @field('batch_number') batchNumber!: string
    @date('mfg_date') mfgDate!: Date | null
    @date('expiry_date') expiryDate!: Date | null
    @field('quantity') quantity!: number
    @field('last_sync_quantity') lastSyncQuantity!: number | null
    @field('cost_price') costPrice!: number | null
    @field('version') version!: number
    @field('is_synced') isSynced!: boolean
    @field('is_deleted') isDeleted!: boolean
    @readonly @date('created_at') createdAt!: Date
    @readonly @date('updated_at') updatedAt!: Date
}

export class Godown extends Model {
    static table = 'godowns'
    static associations = {
        stores: { type: 'belongs_to' as const, key: 'store_id' },
        stock_movements: { type: 'has_many' as const, foreignKey: 'godown_id' },
        stock_transfers: { type: 'has_many' as const, foreignKey: 'from_godown_id' },
    }
    @field('store_id') storeId!: string
    @field('name') name!: string
    @field('address') address!: string | null
    @field('is_default') isDefault!: boolean
    @field('version') version!: number
    @field('is_synced') isSynced!: boolean
    @field('is_deleted') isDeleted!: boolean
    @readonly @date('created_at') createdAt!: Date
    @readonly @date('updated_at') updatedAt!: Date
}

export class StockTransfer extends Model {
    static table = 'stock_transfers'
    static associations = {
        godowns: { type: 'belongs_to' as const, key: 'from_godown_id' },
        stock_transfer_items: { type: 'has_many' as const, foreignKey: 'stock_transfer_id' },
    }
    @field('transfer_number') transferNumber!: string
    @field('from_godown_id') fromGodownId!: string
    @field('to_godown_id') toGodownId!: string
    @field('status') status!: string
    @field('notes') notes!: string | null
    @date('dispatched_at') dispatchedAt!: Date | null
    @date('received_at') receivedAt!: Date | null
    @field('created_by_id') createdById!: string
    @field('version') version!: number
    @field('is_synced') isSynced!: boolean
    @field('is_deleted') isDeleted!: boolean
    @readonly @date('created_at') createdAt!: Date
    @readonly @date('updated_at') updatedAt!: Date

    @relation('godowns', 'from_godown_id') fromGodown: any
    @relation('godowns', 'to_godown_id') toGodown: any
    @children('stock_transfer_items') items: any
}

export class StockTransferItem extends Model {
    static table = 'stock_transfer_items'
    static associations = {
        stock_transfers: { type: 'belongs_to' as const, key: 'stock_transfer_id' },
        products: { type: 'belongs_to' as const, key: 'product_id' },
    }
    @field('stock_transfer_id') stockTransferId!: string
    @field('product_id') productId!: string
    @field('batch_id') batchId!: string | null
    @field('quantity') quantity!: number
    @field('version') version!: number
    @field('is_synced') isSynced!: boolean
    @field('is_deleted') isDeleted!: boolean
    @readonly @date('created_at') createdAt!: Date
    @readonly @date('updated_at') updatedAt!: Date

    @relation('stock_transfers', 'stock_transfer_id') transfer: any
    @relation('products', 'product_id') product: any
}

export class StockMovement extends Model {
    static table = 'stock_movements'
    @field('product_id') productId!: string
    @field('godown_id') godownId!: string | null
    @field('batch_id') batchId!: string | null
    @field('type') type!: string
    @field('quantity') quantity!: number
    @field('reference_id') referenceId!: string | null
    @field('reference_type') referenceType!: string | null
    @field('notes') notes!: string | null
    @field('source') source!: string | null
    @field('user_id') userId!: string | null
    @field('version') version!: number
    @field('is_synced') isSynced!: boolean
    @field('is_deleted') isDeleted!: boolean
    @readonly @date('created_at') createdAt!: Date
    @readonly @date('updated_at') updatedAt!: Date
}

export class Supplier extends Model {
    static table = 'suppliers'
    @field('merchant_id') merchantId!: string
    @field('name') name!: string
    @field('contact_person') contactPerson!: string | null
    @field('phone') phone!: string | null
    @field('email') email!: string | null
    @field('gstin') gstin!: string | null
    @field('address') address!: string | null
    @field('credit_limit') creditLimit!: number
    @field('credit_days') creditDays!: number
    @field('current_balance') currentBalance!: number
    @field('bank_details') bankDetails!: string | null
    @field('tags') tags!: string | null
    @field('version') version!: number
    @field('is_synced') isSynced!: boolean
    @field('is_deleted') isDeleted!: boolean
    @field('last_sync_current_balance') lastSyncCurrentBalance!: number | null
    @readonly @date('created_at') createdAt!: Date
    @readonly @date('updated_at') updatedAt!: Date
}

export class PurchaseOrder extends Model {
    static table = 'purchase_orders'
    static associations = {
        suppliers: { type: 'belongs_to' as const, key: 'supplier_id' },
        po_line_items: { type: 'has_many' as const, foreignKey: 'purchase_order_id' },
        purchase_returns: { type: 'has_many' as const, foreignKey: 'purchase_order_id' },
    }
    @field('store_id') storeId!: string
    @field('supplier_id') supplierId!: string
    @field('po_number') poNumber!: string
    @field('status') status!: string
    @field('total_amount') totalAmount!: number
    @field('notes') notes!: string | null
    @date('expected_date') expectedDate!: Date | null
    @field('supplier_invoice_no') supplierInvoiceNo!: string | null
    @date('supplier_invoice_date') supplierInvoiceDate!: Date | null
    @field('version') version!: number
    @field('is_synced') isSynced!: boolean
    @field('is_deleted') isDeleted!: boolean
    @readonly @date('created_at') createdAt!: Date
    @readonly @date('updated_at') updatedAt!: Date

    @relation('suppliers', 'supplier_id') supplier: any
    @children('po_line_items') lineItems: any
    @children('purchase_returns') purchaseReturns: any
}

export class PoLineItem extends Model {
    static table = 'po_line_items'
    static associations = {
        purchase_orders: { type: 'belongs_to' as const, key: 'purchase_order_id' },
        products: { type: 'belongs_to' as const, key: 'product_id' },
    }
    @field('purchase_order_id') purchaseOrderId!: string
    @field('product_id') productId!: string
    @field('quantity') quantity!: number
    @field('unit_price') unitPrice!: number
    @field('received_qty') receivedQty!: number
    @field('tax_rate') taxRate!: number
    @field('cgst_amount') cgstAmount!: number
    @field('sgst_amount') sgstAmount!: number
    @field('igst_amount') igstAmount!: number
    @field('version') version!: number
    @field('is_synced') isSynced!: boolean
    @field('is_deleted') isDeleted!: boolean
    @readonly @date('created_at') createdAt!: Date
    @readonly @date('updated_at') updatedAt!: Date

    @relation('purchase_orders', 'purchase_order_id') purchaseOrder: any
    @relation('products', 'product_id') product: any
}

export class SupplierPayment extends Model {
    static table = 'supplier_payments'
    static associations = {
        suppliers: { type: 'belongs_to' as const, key: 'supplier_id' },
    }
    @field('store_id') storeId!: string
    @field('supplier_id') supplierId!: string
    @field('amount') amount!: number
    @field('method') method!: string
    @field('transaction_ref') transactionRef!: string | null
    @date('payment_date') paymentDate!: Date
    @field('notes') notes!: string | null
    @field('version') version!: number
    @field('is_synced') isSynced!: boolean
    @field('is_deleted') isDeleted!: boolean
    @readonly @date('created_at') createdAt!: Date
    @readonly @date('updated_at') updatedAt!: Date

    @relation('suppliers', 'supplier_id') supplier: any
}

export class PurchaseReturn extends Model {
    static table = 'purchase_returns'
    static associations = {
        suppliers: { type: 'belongs_to' as const, key: 'supplier_id' },
        purchase_orders: { type: 'belongs_to' as const, key: 'purchase_order_id' },
        purchase_return_items: { type: 'has_many' as const, foreignKey: 'purchase_return_id' },
    }
    @field('store_id') storeId!: string
    @field('supplier_id') supplierId!: string
    @field('purchase_order_id') purchaseOrderId!: string | null
    @field('return_number') returnNumber!: string
    @field('status') status!: string
    @field('reason') reason!: string | null
    @field('total_amount') totalAmount!: number
    @field('notes') notes!: string | null
    @field('version') version!: number
    @field('is_synced') isSynced!: boolean
    @field('is_deleted') isDeleted!: boolean
    @readonly @date('created_at') createdAt!: Date
    @readonly @date('updated_at') updatedAt!: Date

    @relation('suppliers', 'supplier_id') supplier: any
    @relation('purchase_orders', 'purchase_order_id') purchaseOrder: any
    @children('purchase_return_items') items: any
}

export class PurchaseReturnItem extends Model {
    static table = 'purchase_return_items'
    static associations = {
        purchase_returns: { type: 'belongs_to' as const, key: 'purchase_return_id' },
        products: { type: 'belongs_to' as const, key: 'product_id' },
        batches: { type: 'belongs_to' as const, key: 'batch_id' },
    }
    @field('purchase_return_id') purchaseReturnId!: string
    @field('product_id') productId!: string
    @field('batch_id') batchId!: string | null
    @field('quantity') quantity!: number
    @field('unit_price') unitPrice!: number
    @field('reason') reason!: string | null
    @field('version') version!: number
    @field('is_synced') isSynced!: boolean
    @field('is_deleted') isDeleted!: boolean
    @readonly @date('created_at') createdAt!: Date
    @readonly @date('updated_at') updatedAt!: Date

    @relation('purchase_returns', 'purchase_return_id') purchaseReturn: any
    @relation('products', 'product_id') product: any
    @relation('batches', 'batch_id') batch: any
}

export class SupplierImportMapping extends Model {
    static table = 'supplier_import_mappings'
    static associations = {
        suppliers: { type: 'belongs_to' as const, key: 'supplier_id' },
        merchants: { type: 'belongs_to' as const, key: 'merchant_id' },
    }
    @field('merchant_id') merchantId!: string
    @field('supplier_id') supplierId!: string
    @field('mapping_name') mappingName!: string
    @field('column_mappings') columnMappings!: string  // JSON string
    @field('file_type') fileType!: string
    @field('header_row') headerRow!: number
    @field('skip_rows') skipRows!: number
    @field('notes') notes!: string | null
    @field('is_default') isDefault!: boolean
    @field('version') version!: number
    @field('is_synced') isSynced!: boolean
    @field('is_deleted') isDeleted!: boolean
    @readonly @date('created_at') createdAt!: Date
    @readonly @date('updated_at') updatedAt!: Date

    @relation('suppliers', 'supplier_id') supplier: any
    @relation('merchants', 'merchant_id') merchant: any
}

export class SupplierLedger extends Model {
    static table = 'supplier_ledger'
    static associations = {
        suppliers: { type: 'belongs_to' as const, key: 'supplier_id' },
        merchants: { type: 'belongs_to' as const, key: 'merchant_id' },
    }
    @field('merchant_id') merchantId!: string
    @field('supplier_id') supplierId!: string
    @date('transaction_date') transactionDate!: Date
    @field('type') type!: string
    @field('amount') amount!: number
    @field('balance_after') balanceAfter!: number
    @field('reference_id') referenceId!: string | null
    @field('reference_type') referenceType!: string | null
    @field('notes') notes!: string | null
    @field('version') version!: number
    @field('is_synced') isSynced!: boolean
    @field('is_deleted') isDeleted!: boolean
    @readonly @date('created_at') createdAt!: Date
    @readonly @date('updated_at') updatedAt!: Date

    @relation('suppliers', 'supplier_id') supplier: any
    @relation('merchants', 'merchant_id') merchant: any
}
